import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  message,
  Steps,
  Typography,
  Row,
  Col,
  GetProp,
  UploadProps,
  Alert,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import LogoUpload from "components/logo-upload";
import { useState, useEffect, useMemo } from "react";
import venueWorkingHours from "../collab/weekHoursDefault.json";
import { PlusCircleOutlined, DeleteFilled } from "@ant-design/icons";
import {
  generateBusinessDescriptionByWebsite,
  obtainBusinessLogoByWebsite,
  uploadLogo,
  removeLogo,
  updateBusinessInfo,
  finishBusinessOnboarding,
} from "services/SauceService";
import ConnectSocial from "components/connect-social";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "components/loader/Loader";
import { getBusinessInfo } from "redux-context/business";
import VenueFormFields from "components/venue-form-fields/VenueFormFields";
import { normalizeWebsiteURL } from "../../helpers/url-helpers";
import "./OnboardingForm.scss";

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const OnboardingForm = () => {
  const [current, setCurrent] = useState(0);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [websiteLoading, setWebsiteLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [file, setFile] = useState<any>(undefined);
  const [lastProcessedWebsite, setLastProcessedWebsite] = useState<string>("");
  const {
    business,
    loading: businessLoading,
    subscription,
  } = useSelector((state: any) => state.business);

  const isValidURL = (rawUrl: string): boolean =>
    normalizeWebsiteURL(rawUrl) !== null;

  const handleWebsiteBlur = async (ev: React.FocusEvent<HTMLInputElement>) => {
    const websiteInput = ev.target.value.trim();
    if (!websiteInput) return;

    const formValues = form.getFieldsValue(true);

    const normalizedWebsiteURL = normalizeWebsiteURL(websiteInput);

    if (!normalizedWebsiteURL) {
      message.warning(
        "Please enter a valid URL (e.g., https://www.example.com)"
      );
      return;
    }

    const normalizedBusinessWebsite = normalizeWebsiteURL(
      business?.website ?? ""
    );

    if (
      normalizedWebsiteURL === lastProcessedWebsite ||
      normalizedWebsiteURL === normalizedBusinessWebsite
    )
      return;

    setWebsiteLoading(true);
    try {
      const currentDescription = formValues.aboutTheBrand;
      const currentLogoUrl = imageUrl;
      const promises: any[] = [];

      if (!currentDescription) {
        promises.push(
          generateBusinessDescriptionByWebsite({
            websiteURL: normalizedWebsiteURL,
          }).then((response) => ({
            type: "aboutTheBrand",
            data: response,
          }))
        );
      }

      if (!currentLogoUrl) {
        promises.push(
          obtainBusinessLogoByWebsite({
            websiteURL: normalizedWebsiteURL,
          }).then((response) => ({ type: "logo", data: response }))
        );
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);

        let newValues: any = {};
        results.forEach((result) => {
          if (result.type === "aboutTheBrand") {
            newValues.aboutTheBrand = result.data.description;
          } else if (result.type === "logo") {
            newValues.logoUrl = result.data.logoUrl;
            setImageUrl(result.data.logoUrl);
          }
        });

        form.setFieldsValue({
          ...formValues,
          ...newValues,
        });
      }

      setLastProcessedWebsite(normalizedWebsiteURL);
    } catch (error) {
      console.error("Error fetching business data:", error);
    } finally {
      setWebsiteLoading(false);
    }
  };

  const handleLogoChange = (
    selectedFile: any,
    previewUrl: string | undefined
  ) => {
    if (typeof previewUrl !== "string" && previewUrl !== undefined) {
      console.warn(
        "LogoUpload onChange received invalid previewUrl:",
        previewUrl
      );
      console.warn("selectedFile:", selectedFile);
      return;
    }
    if (previewUrl && !previewUrl.startsWith("data:image/")) {
      console.warn("Preview URL doesn't look like a base64 image:", previewUrl);
      return;
    }
    setFile(selectedFile);
    setImageUrl(previewUrl || "");
  };

  const handleUpload = async (file: FileType) => {
    const params = { logo: file, businessId: business._id };
    try {
      await uploadLogo(params);
    } catch (error) {
      console.error(error);
      message.error("Failed to upload logo");
    }
  };

  const handleRemoveImage = async () => {
    try {
      await removeLogo({ businessId: business._id });
    } catch (error) {
      console.error(error);
    }
  };

  const handleFinish = async () => {
    setWebsiteLoading(true);
    try {
      const { venues } = await form.validateFields();
      const venuesData = venues.map((x: any) => ({
        ...x,
        placeId: x.googlePlaceId,
        placeName: x.name,
        placeAddress: x.addressDisplay,
        id: x._id,
      }));

      await finishBusinessOnboarding({
        ...business,
        venues: venuesData,
        businessId: business._id,
      });
      await dispatch(getBusinessInfo());
      navigate("/");
    } catch (error) {
      console.error(error);
      message.error("Failed to finish onboarding");
    } finally {
      setWebsiteLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
    setImageUrl(business?.logoUrl);
  }, [business, form]);

  const steps = [
    {
      title: "Business Info",
      content: (
        <Flex vertical>
          <Flex vertical>
            <p className="step-section-title">Tell us about your business</p>
            <p className="step-section-subtitle">
              We'll use this information to help match you with the right
              creators
            </p>
          </Flex>
          <Form.Item
            label="Business Name"
            rules={[
              {
                required: true,
              },
            ]}
            name="businessName"
          >
            <Input
              placeholder="Enter your business name"
              className="campaign-venues business-name-input"
              autoComplete="off"
              disabled={websiteLoading}
            />
          </Form.Item>
          <Form.Item
            name="website"
            label="Website"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return isValidURL(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Please enter a valid URL!"));
                },
              },
            ]}
          >
            <Input
              placeholder="https://www.yourbusiness.com"
              disabled={websiteLoading}
              onBlur={handleWebsiteBlur}
            />
          </Form.Item>
          <Form.Item name="aboutTheBrand" label="Description">
            <TextArea
              rows={8}
              placeholder="Tell us about your business, what you do, and what makes you unique..."
              disabled={websiteLoading}
            />
          </Form.Item>
          <Form.Item label="Logo">
            <LogoUpload
              value={imageUrl}
              onChange={handleLogoChange}
              loading={websiteLoading}
              maxSizeMB={10}
            />
          </Form.Item>
        </Flex>
      ),
    },
    {
      title: "Social Media",
      content: (
        <Flex vertical gap={10}>
          <Flex vertical>
            <p className="step-section-title">Connect Your Social Media</p>
            <p className="step-section-subtitle">
              Linking your social media lets creators tag your brand in posts
              and helps Mustard measure collaboration reach and results.
            </p>
          </Flex>

          <Row gutter={[0, 24]}>
            <Col span={24}>
              <ConnectSocial.Instagram />
            </Col>
            <Col span={24}>
              <ConnectSocial.TikTok />
            </Col>
            <Col span={24}>
              <Alert
                message="Connect all your social media accounts."
                description="When prompted, select every account that will be used for collaborations. If your brand manages separate accounts per location, make sure to include each one."
                type="info"
                showIcon
                className="connect-instagram-alert"
              />
            </Col>
          </Row>
        </Flex>
      ),
    },
    {
      title: "Locations",
      content: (
        <Flex vertical gap={10}>
          <Flex vertical>
            <p className="step-section-title">Add Your Business Locations</p>
            <p className="step-section-subtitle">
              Add at least one location so creators know where to find you.
            </p>
          </Flex>

          <Form.List name="venues">
            {(fields, { add, remove }) => (
              <div className="venues-list-container">
                {fields.map((field, index) => (
                  <Card
                    key={`venue_${field.name}`}
                    title={
                      <Flex justify="space-between" align="center">
                        <span>{`Location ${index + 1}`}</span>
                        {fields.length > 1 && (
                          <DeleteFilled
                            className="delete-location-icon"
                            onClick={() => remove(field.name)}
                          />
                        )}
                      </Flex>
                    }
                    className="location-card"
                  >
                    <VenueFormFields
                      newSocialMediaFields={true}
                      key={`venue_${field.name}`}
                      namePath={[field.name]}
                      formListNameFullPath={["venues"]}
                      business={business}
                      hiddenCopyWorkingHours
                    />
                  </Card>
                ))}

                {fields.length < subscription?.locationsAllowed && (
                  <span
                    className="add-location-button"
                    onClick={() =>
                      add({
                        pocs: [
                          {
                            name: "",
                            email: "",
                            notifyEmail: false,
                            phone: "",
                          },
                        ],
                        venueWorkingHours,
                      })
                    }
                  >
                    <PlusCircleOutlined />
                    Add another location
                  </span>
                )}
              </div>
            )}
          </Form.List>
        </Flex>
      ),
    },
  ];

  const firstStepHandler = async (values: any) => {
    if (file) await handleUpload(file);
    if (business?.logoUrl && !imageUrl) await handleRemoveImage();

    await updateBusinessInfo({
      ...business,
      ...values,
      businessId: business._id,
      logoUrl: imageUrl,
    });
    dispatch(getBusinessInfo());
  };

  const next = async () => {
    try {
      const values = await form.validateFields();
      if (current === 0) await firstStepHandler(values);
      setCurrent(current + 1);
    } catch (error) {
      console.error("Validation Error:", error);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const formInitialValues = useMemo(
    () => ({
      ...business,
      venues: [
        {
          pocs: [
            {
              name: "",
              email: "",
              notifyEmail: false,
              phone: "",
            },
          ],
          venueWorkingHours,
        },
      ],
    }),
    [business]
  );

  return (
    <Flex align="center" justify="center" vertical className="onboarding-form">
      <Loader visible={websiteLoading || businessLoading} />
      <Flex vertical className="header-section">
        <Typography.Title level={1} className="main-title">
          Get Started With Content Marketing
        </Typography.Title>
        <Typography.Title level={4} className="main-subtitle">
          Complete the setup process to start connecting with creators and grow
          your business through authentic partnerships
        </Typography.Title>
      </Flex>
      <Steps current={current} className="steps-container">
        {steps.map((item, index) => (
          <Steps.Step
            key={item.title}
            title={item.title}
            status={current < index ? "wait" : "process"}
          />
        ))}
      </Steps>
      <Card className="form-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={formInitialValues}
          className="onboarding-form-container"
        >
          <div className="form-content-wrapper">{steps[current].content}</div>

          <div
            className={`button-container ${current > 0 ? "with-previous" : ""}`}
          >
            {current > 0 && (
              <Button
                className="previous-button"
                onClick={prev}
                disabled={websiteLoading}
              >
                Previous
              </Button>
            )}
            {current < steps.length - 1 && (
              <Button
                type="primary"
                onClick={next}
                disabled={websiteLoading}
                loading={websiteLoading}
              >
                Continue
              </Button>
            )}
            {current === steps.length - 1 && (
              <Button
                type="primary"
                htmlType="submit"
                disabled={websiteLoading}
                loading={websiteLoading}
              >
                Continue
              </Button>
            )}
          </div>
        </Form>
      </Card>
    </Flex>
  );
};

export default OnboardingForm;
